import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  FiChevronLeft,
  FiChevronRight,
  FiSave,
  FiX,
  FiEye,
} from "react-icons/fi";
import { formAPI } from "../../../../../services/api";
import InitialPage from "./pages/InitialPage";
import ICUPage from "./pages/ICUPage";
import DailyPage from "./pages/DailyPage";
import AddDailyPage from "./pages/AddDailyPage";
import EarlyOutcome from "./pages/EarlyOutcome";
import HospDischarge from "./pages/HospDischarge";
import Surgeries from "./pages/Surgeries";
import Outcome from "./pages/Outcome";
import "./11NewCase.css";

const totalSteps = 9;
const stepLabels = [
  "Initial",
  "ICU",
  "Daily",
  "Additional Daily",
  "Early Outcome",
  "Hosp Discharge",
  "Surgeries",
  "Outcome",
];

const VIP11ReadOnly = ({ projectId, initialFormData }) => {
  const navigate = useNavigate();
  const { dataId } = useParams();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isPanelExpanded, setIsPanelExpanded] = useState(false);
  const [userValue, setUserValue] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState(null);

  // 읽기 전용 스타일
  const readOnlyStyle = {
    backgroundColor: "#f5f5f5",
    cursor: "not-allowed",
    opacity: 0.7,
  };

  // 읽기 전용 배너
  const ReadOnlyBanner = () => (
    <div
      style={{
        backgroundColor: "#e9ecef",
        color: "#495057",
        padding: "8px 16px",
        textAlign: "center",
        position: "sticky",
        top: 0,
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        borderBottom: "1px solid #dee2e6",
      }}
    >
      <FiEye />
      <span>읽기 전용 모드</span>
    </div>
  );

  useEffect(() => {
    console.group("[VIP11ReadOnly] Component Mount Effect");
    console.log("Initial Props:", { dataId, projectId });
    console.log("Location State:", location.state);
    console.log("Current FormData:", formData);

    if (!location.state?.formData) {
      console.log("No FormData in state, fetching from API");
      fetchFormData();
    } else {
      console.log("Using FormData from state");
      setFormData(location.state.formData);
    }
    console.groupEnd();
  }, []);

  const fetchFormData = async () => {
    if (!dataId) {
      setError("데이터 ID가 없습니다.");
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      console.log("[VIP11ReadOnly] Fetching data from API for dataId:", dataId);

      const response = await formAPI.getFormDataById(dataId);

      if (!response || !response.data) {
        throw new Error("데이터를 불러올 수 없습니다.");
      }

      // 날짜 형식 변환 (T → 공백)
      const processedData = Object.entries(response.data).reduce(
        (acc, [key, value]) => {
          if (typeof value === "string" && value.includes("T")) {
            acc[key] = value.replace("T", " ");
          } else {
            acc[key] = value;
          }
          return acc;
        },
        {}
      );

      console.log("[VIP11ReadOnly] Setting form data:", processedData);
      setFormData(processedData);
    } catch (error) {
      console.error("[VIP11ReadOnly] Error fetching data:", error);
      setError(error.message || "데이터를 불러오는 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // 에러 발생 시 표시할 컴포넌트
  const ErrorComponent = () => (
    <div
      className="error-container"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "calc(100vh - 80px)",
        padding: "20px",
        backgroundColor: "#fff",
      }}
    >
      <div
        style={{
          backgroundColor: "#fff3f3",
          border: "1px solid #ffcdd2",
          borderRadius: "8px",
          padding: "20px",
          maxWidth: "500px",
        }}
      >
        <h2 style={{ color: "#d32f2f", marginBottom: "15px" }}>오류 발생</h2>
        <p style={{ color: "#555", marginBottom: "20px" }}>{error}</p>
        <button
          onClick={() => navigate("/ecrf/read")}
          style={{
            backgroundColor: "#6c757d",
            color: "white",
            border: "none",
            padding: "10px 20px",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          목록으로 돌아가기
        </button>
      </div>
    </div>
  );

  // 로딩 중 표시할 컴포넌트
  const LoadingComponent = () => (
    <div
      className="loading-container"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "calc(100vh - 80px)",
        padding: "20px",
        backgroundColor: "#fff",
      }}
    >
      <div
        className="loading-spinner"
        style={{
          border: "4px solid #f3f3f3",
          borderTop: "4px solid #3498db",
          borderRadius: "50%",
          width: "50px",
          height: "50px",
          animation: "spin 1s linear infinite",
          marginBottom: "20px",
        }}
      ></div>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <p style={{ color: "#666", fontSize: "16px" }}>
        데이터를 불러오는 중입니다...
      </p>
    </div>
  );

  // 스크롤 이벤트 리스너
  useEffect(() => {
    const handleScroll = () => {
      const ecrfContent = document.querySelector(".ecrf-content");
      const scrollTop = ecrfContent ? ecrfContent.scrollTop : window.scrollY;
      setShowScrollButton(scrollTop > 300);
    };

    const ecrfContent = document.querySelector(".ecrf-content");
    const targetElement = ecrfContent || window;

    targetElement.addEventListener("scroll", handleScroll);
    return () => targetElement.removeEventListener("scroll", handleScroll);
  }, []);

  // 맨 위로 스크롤
  const goTop = () => {
    const ecrfContent = document.querySelector(".ecrf-content");

    if (ecrfContent) {
      ecrfContent.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }
  };

  // StepIndicator 컴포넌트
  const StepIndicator = () => (
    <div className="vip-step-header">
      <div className="vip-step-indicator">
        {stepLabels.map((label, index) => (
          <div
            key={index}
            className={`step-item ${
              currentStep === index + 1 ? "active" : ""
            } ${currentStep > index + 1 ? "completed" : ""}`}
            onClick={() => setCurrentStep(index + 1)}
            style={readOnlyStyle}
          >
            <div className="step-number">{index + 1}</div>
            <div className="step-label">{label}</div>
          </div>
        ))}
      </div>
    </div>
  );

  // StepActions 컴포넌트
  const StepActions = () => (
    <div className="vip-step-actions">
      <div className="navigation-buttons">
        <button
          type="button"
          className={`vip-step-button prev ${
            currentStep === 1 ? "disabled" : ""
          }`}
          onClick={() => setCurrentStep((p) => Math.max(1, p - 1))}
          disabled={currentStep === 1}
        >
          <FiChevronLeft size={16} />
          이전
        </button>

        <button
          type="button"
          className={`vip-step-button next ${
            currentStep === totalSteps ? "disabled" : ""
          }`}
          onClick={() => setCurrentStep((p) => Math.min(totalSteps, p + 1))}
          disabled={currentStep === totalSteps}
        >
          다음
          <FiChevronRight size={16} />
        </button>
      </div>

      <div className="save-button-container">
        <button
          type="button"
          className="vip-submit-button large disabled"
          disabled={true}
          style={readOnlyStyle}
        >
          <FiSave size={16} />
          읽기 전용
        </button>
      </div>
    </div>
  );

  // renderStep 함수
  const renderStep = () => {
    if (!formData) {
      return <LoadingComponent />;
    }

    const readOnlyProps = {
      formData,
      handleChange: () => {}, // 빈 함수
      handleCheckboxChange: () => {}, // 빈 함수
      dtDisplay: (v) => (typeof v === "string" ? v.replace("T", " ") : v),
      readOnly: true,
    };

    switch (currentStep) {
      case 1:
        return (
          <>
            <div className="data_id_container">
              <div className="data_id_value">{formData.data_id}</div>
            </div>
            <InitialPage {...readOnlyProps} />
          </>
        );
      case 2:
        return (
          <>
            <div className="data_id_container">
              <div className="data_id_value">{formData.data_id}</div>
            </div>
            <ICUPage {...readOnlyProps} />
          </>
        );
      case 3:
        return (
          <>
            <div className="data_id_container">
              <div className="data_id_value">{formData.data_id}</div>
            </div>
            <DailyPage {...readOnlyProps} />
          </>
        );
      case 4:
        return (
          <>
            <div className="data_id_container">
              <div className="data_id_value">{formData.data_id}</div>
            </div>
            <AddDailyPage {...readOnlyProps} />
          </>
        );
      case 5:
        return (
          <>
            <div className="data_id_container">
              <div className="data_id_value">{formData.data_id}</div>
            </div>
            <EarlyOutcome {...readOnlyProps} />
          </>
        );
      case 6:
        return (
          <>
            <div className="data_id_container">
              <div className="data_id_value">{formData.data_id}</div>
            </div>
            <HospDischarge {...readOnlyProps} />
          </>
        );
      case 7:
        return (
          <>
            <div className="data_id_container">
              <div className="data_id_value">{formData.data_id}</div>
            </div>
            <Surgeries {...readOnlyProps} />
          </>
        );
      case 8:
        return (
          <>
            <div className="data_id_container">
              <div className="data_id_value">{formData.data_id}</div>
            </div>
            <Outcome {...readOnlyProps} />
          </>
        );
      default:
        return (
          <section className="vip-form-section">
            <h2 className="section-title">페이지를 찾을 수 없습니다</h2>
          </section>
        );
    }
  };

  if (isLoading) {
    return <LoadingComponent />;
  }

  if (error) {
    return <ErrorComponent />;
  }

  return (
    <div className="vip-new-case">
      <ReadOnlyBanner />
      <StepIndicator />
      {renderStep()}
      <StepActions />

      {showScrollButton && (
        <button
          onClick={goTop}
          style={{
            position: "fixed",
            right: "24px",
            bottom: "24px",
            width: "50px",
            height: "50px",
            borderRadius: "50%",
            backgroundColor: "#007bff",
            color: "#fff",
            border: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "20px",
            zIndex: 1000,
            marginBottom: "200px",
          }}
        >
          ↑
        </button>
      )}
    </div>
  );
};

export default VIP11ReadOnly;
